import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useScrollAnimation from '../hooks/useScrollAnimation';
import { fadeInUp, staggerContainer } from '../utils/animationVariants';

const FAQ = () => {
  const { ref, controls } = useScrollAnimation(0.1);
  const [activeIndex, setActiveIndex] = useState(null);
  
  const faqs = [
    {
      question: 'What is a digital business card?',
      answer: 'A digital business card revolutionizes professional networking by replacing outdated paper cards with a powerful online profile. Your contact information, credentials, and social links come alive in an interactive format that impresses at first sight. Share instantly through QR codes, links, or cutting-edge NFC technology. Update anything in real-time-no more reprinting costs or outdated information. The smart choice for professionals who value both efficiency and sustainability.'
    },
    {
      question: 'How do I create and customize my digital card?',
      answer: 'Transform your professional presence in under 5 minutes! Our intuitive interface guides you through every step-simply sign up, input your information, and watch as professionally designed templates bring your brand to life. Match your exact brand colors, showcase your company logo prominently, and connect all your social profiles seamlessly. Premium members unlock the full creative potential with advanced customization features that make your card truly unforgettable.'
    },
    {
      question: 'What\'s the difference between Free and Premium plans?',
      answer: 'Free gets you started with the essentials: one digital card, QR code sharing, and basic contact saving. But Premium? That\'s where professionals truly shine. Multiple cards for different occasions, a comprehensive analytics dashboard that reveals networking insights, seamless NFC card integration, priority support when you need it, and an exclusive premium profile badge that sets you apart. Serious networkers choose Premium because effective lead generation demands powerful tools.'
    },
    {
      question: 'How do I share my digital business card?',
      answer: 'Every networking moment becomes an opportunity with AM CARDMATE\'s versatile sharing options. Flash your QR code at events for instant connections, embed your link in email signatures for passive networking, tap phones using NFC for that "wow" factor, or share across social media to expand your reach. Recipients save your info with just one click-no app downloads, no friction. This seamless experience dramatically increases engagement rates and ensures your connections actually follow through.'
    },
    {
      question: 'Can I track who views my card?',
      answer: 'Premium analytics transform guesswork into strategy. Monitor your networking impact with comprehensive metrics: total views, unique visitors, QR code scan rates, peak engagement times, individual link performance, and crucial contact save rates. Watch patterns emerge that reveal which strategies work best for your goals. Fresh data keeps you informed, always optimizing, always one step ahead in your networking game.'
    },
    {
      question: 'How does contact management work?',
      answer: 'Turn networking chaos into organized opportunity! Our built-in scanner instantly captures contacts at events-just scan QR codes or business cards and watch them flow into custom collections. Create meaningful groups like "Tech Conference 2025" or "Potential Partners" to keep connections contextual and actionable. Every contact stays perfectly organized within the app, ending the era of lost cards and forgotten follow-ups. One-click CSV export or workflow integration means your networking efforts seamlessly connect with your business processes.'
    },
    {
      question: 'Is my information secure?',
      answer: 'Your security isn\'t just a feature-it\'s our foundation. Enterprise-grade encryption protects every byte of your data, while secure cloud infrastructure with automated backups ensures it\'s always safe and accessible. Full GDPR and CCPA compliance isn\'t just checkbox-it\'s our commitment. Regular security audits keep us ahead of threats. You control exactly what\'s visible and to whom. Complete data portability and deletion rights put you in charge. We never sell user data, period. Strict internal access controls mean your information stays yours alone.'
    },
    {
      question: 'Do recipients need the app to view my card?',
      answer: 'Zero barriers mean maximum connections. Recipients view your stunning digital card instantly on any device\'s web browser-no downloads, no sign-ups, no excuses. They save your contact directly to their phone\'s native contact list with a single tap. This friction-free approach delivers engagement rates that leave app-dependent solutions behind. The AM CARDMATE app? That\'s exclusively for professionals ready to create and manage their own digital presence. Your contacts just enjoy the seamless experience.'
    }
  ];

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-24 bg-white relative">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(77,162,255,0.05)_0%,transparent_50%),radial-gradient(circle_at_20%_80%,rgba(222,97,97,0.05)_0%,transparent_50%)]"></div>
      
      <div className="container mx-auto px-6 relative z-10">
        <motion.div 
          ref={ref}
          className="text-center max-w-3xl mx-auto mb-16"
          variants={fadeInUp}
          initial="hidden"
          animate={controls}
        >
          <div className="badge badge-pink inline-flex mb-4">
            <FontAwesomeIcon icon="question-circle" />
            <span>Have Questions?</span>
          </div>
          
          <h2 className="section-title">
            Frequently Asked <span className="text-gradient-blue-pink">Questions</span>
          </h2>
          
          <p className="section-description">
            Everything you need to know about digital business cards and AM CARDMATE
          </p>
        </motion.div>
        
        <motion.div 
          className="max-w-3xl mx-auto"
          variants={staggerContainer}
          initial="hidden"
          animate={controls}
        >
          {faqs.map((faq, index) => (
            <motion.div 
              key={index}
              className={`bg-white rounded-xl mb-6 shadow-md hover:shadow-lg hover:-translate-y-1 transition-all duration-300 overflow-hidden border border-transparent hover:border-blue-100/30 ${activeIndex === index ? 'shadow-lg' : ''}`}
              variants={fadeInUp}
              onClick={() => toggleFAQ(index)}
            >
              <div className="p-8 cursor-pointer flex justify-between items-center">
                <h3 className="text-h5 text-dark pr-4">{faq.question}</h3>
                
                <div className={`w-9 h-9 rounded-full ${activeIndex === index ? 'bg-gradient-blue text-white shadow-blue' : 'bg-blue-100/50 text-primary-blue'} flex items-center justify-center transition-all duration-300 flex-shrink-0`}>
                  <FontAwesomeIcon 
                    icon="chevron-down" 
                    className={`transition-transform duration-300 ${activeIndex === index ? 'rotate-180' : ''}`}
                  />
                </div>
              </div>
              
              <AnimatePresence>
                {activeIndex === index && (
                  <motion.div 
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="px-8 pb-8">
                      <p className="text-body text-gray">{faq.answer}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </motion.div>

        <motion.div 
          className="text-center mt-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p className="text-body text-gray mb-6">Still have questions?</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.a
              href="/help"
              className="btn btn-primary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FontAwesomeIcon icon="life-ring" />
              Visit Help Center
            </motion.a>
            <motion.a
              href="mailto:support@amcardmate.com"
              className="btn btn-outline"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FontAwesomeIcon icon="envelope" />
              Contact Support
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;